import { FormikProps, useFormik } from "formik";
import { useCallback, useEffect, useMemo, useState } from "react";
import useCurrentUser from "@/hooks/local-storage/use-current-user";
import { useSnackbar } from "@/hooks/useSnackbar";
import {
  FORM2PAYLOAD_INITIAL_VALUE,
  type Form2Payload,
} from "@/pages/app/provider/scheduling/appointment/encounter/encounter-constant";
import { useEncounterCreate } from "@/queries/encounter/use-encounter-create";
import { useEncounterSummeryV2 } from "@/queries/encounter/use-encounter-summery-v2";
import { useEncounterUpdate } from "@/queries/encounter/use-encounter-update";
import {
  getErrorSnackbar,
  getSuccessSnackbar,
  getXtenantId,
} from "@/utils/utils";
import {
  GroupNoteTemplate,
  SavedEncounterNote,
  SessionParticipant,
} from "../group-visit-types";
import {
  getGroupNoteTemplate,
  isEncounterSignedOff,
  toIndividualNotePayload,
  toIndividualNoteValues,
} from "../group-visit-utils";

export type UseIndividualNote = {
  formik: FormikProps<Form2Payload>;
  /** Visit note template the note is documented on. */
  template: GroupNoteTemplate;
  setTemplate: (value: GroupNoteTemplate) => void;
  isLoading: boolean;
  isSaving: boolean;
  /** Signed off: the note is read-only from here on. */
  isSignedOff: boolean;
  /** Writes the note, the way the individual visit note writes it. */
  save: () => Promise<void>;
  /** The saved encounter summary the sign-off drawer signs. */
  summary?: SavedEncounterNote;
  /** Re-reads the note after it has been signed. */
  refresh: () => void;
};

/**
 * One participant's visit note, stored as the encounter summary of their
 * appointment and saved exactly as the individual visit note saves it.
 *
 * @author Rajkumar Rathod
 */
export const useIndividualNote = (
  participant?: SessionParticipant,
  isLocked = false,
  groupType?: string | null
): UseIndividualNote => {
  const showSnackbar = useSnackbar();
  const [currentUser] = useCurrentUser();
  const providerId = currentUser?.uuid;

  const {
    data: snapshot,
    isLoading,
    refetch,
  } = useEncounterSummeryV2({
    appointmentUuid: participant?.uuid,
    xTENANTID: getXtenantId(),
  });
  // The snapshot endpoint returns the record itself, not a page of them.
  const summary = snapshot?.data as SavedEncounterNote | undefined;

  const { mutateAsync: createEncounter, isPending: isCreating } =
    useEncounterCreate();
  const { mutateAsync: updateEncounter, isPending: isUpdating } =
    useEncounterUpdate();

  const [template, setTemplate] = useState<GroupNoteTemplate>(() =>
    getGroupNoteTemplate(groupType)
  );
  const [loadedFor, setLoadedFor] = useState<string | undefined>();
  const [loadedSummary, setLoadedSummary] = useState<SavedEncounterNote>();

  const isSignedOff = isEncounterSignedOff(summary);
  const isReadOnly = isLocked || isSignedOff;

  const formik = useFormik<Form2Payload>({
    initialValues: FORM2PAYLOAD_INITIAL_VALUE,
    onSubmit: () => undefined,
  });
  const { values, resetForm } = formik;

  // Re-applied whenever the participant or their snapshot changes, as the
  // individual visit note prefills from every snapshot it receives.
  useEffect(() => {
    if (!participant || isLoading) return;
    if (loadedFor === participant.uuid && loadedSummary === summary) return;
    const next = getGroupNoteTemplate(groupType, summary?.formType);
    resetForm({ values: toIndividualNoteValues(summary, next) });
    setTemplate(next);
    setLoadedFor(participant.uuid);
    setLoadedSummary(summary);
  }, [
    participant,
    isLoading,
    summary,
    loadedFor,
    loadedSummary,
    groupType,
    resetForm,
  ]);

  // Updates the record when it already holds this template and creates one
  // otherwise — the rule the individual visit note saves by.
  const save = useCallback(async () => {
    if (!participant || isReadOnly) return;
    const shouldUpdate = !!summary?.uuid && summary?.formType === template;
    const payload = {
      encounterSumary: toIndividualNotePayload(values, {
        participant,
        providerId,
        summary: shouldUpdate ? summary : undefined,
        template,
      }),
      xTENANTID: getXtenantId(),
    };

    try {
      const res = await (shouldUpdate
        ? updateEncounter(payload)
        : createEncounter(payload));
      showSnackbar(getSuccessSnackbar(res));
      await refetch();
    } catch (err) {
      showSnackbar(getErrorSnackbar(err));
    }
  }, [
    participant,
    isReadOnly,
    summary,
    template,
    values,
    providerId,
    updateEncounter,
    createEncounter,
    showSnackbar,
    refetch,
  ]);

  return useMemo(
    () => ({
      formik,
      template,
      setTemplate,
      isLoading: !!participant && isLoading,
      isSaving: isCreating || isUpdating,
      isSignedOff,
      save,
      summary,
      refresh: refetch,
    }),
    [
      formik,
      template,
      participant,
      isLoading,
      isCreating,
      isUpdating,
      isSignedOff,
      save,
      summary,
      refetch,
    ]
  );
};
